import React, {Component, createRef} from 'react'
import propTypes from 'prop-types'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import AsyncCreatableSelect from 'react-select/async-creatable'
import {api} from 'services/api'
import {get, isEmpty, isNil} from 'lodash'
import {getWindowScrollPosition, prefix} from 'helpers/component'
import Option from './components/option'
import Value from './components/value'
import {getValue, getStyle} from 'helpers/selectField'
import themeVars from 'theme/themeVars'
import {t} from "helpers/language"
import './selectField.scss'

const getClassName = prefix('selectField-')

export class SelectField extends Component {
  constructor(props) {
    super(props)
    this.selectRef = createRef()
  }

  focus = () => {
    if (this.selectRef.current) this.selectRef.current.focus()
  }

  getProps = (props) => props

  getPropsWithOptions = (props) => {
    const options = get(props, 'options', [])
    props['options'] = isNil(options) ? [] : options
    return props
  }

  getSelect = (props, async, creatable) => {

    if (async) {
      return creatable
        ? <AsyncCreatableSelect {...props}/>
        : <AsyncSelect {...props} />
    }

    props = this.getPropsWithOptions(props)

    return creatable
      ? <CreatableSelect {...props}/>
      : <Select {...props}/>
  }

  getValueComponent = (props) => {
    const {ValueRenderer} = this.props
    if (ValueRenderer) return ValueRenderer
    if (!props.multi) return Value
    return null
  }

  getOptionComponent = () => {
    const {OptionRenderer, isOptionDisabled} = this.props

    if (OptionRenderer) {
      return ({data, children, ...otherProps}) => {
        if (isOptionDisabled)
          data.isDisabled = isOptionDisabled(data)

        return (
          <Option data={{...data, className: undefined}} {...otherProps}>
            <OptionRenderer option={data}>
              {children}
            </OptionRenderer>
          </Option>
        )
      }
    }

    return Option
  }

  getStyles = (props) => {
    const {menuWidth, flat, width, showSeparator, showArrow, borderRadius, backgroundColor, styles} = props
    return {
      ...styles,
      menuPortal: getStyle('menuPortal', styles, (provided) => ({
        zIndex: 999999,
        left: parseInt(provided.left) + getWindowScrollPosition().scrollLeft,
      })),
      menu: getStyle('menu', styles, () => ({
        minWidth: menuWidth,
        backgroundColor: themeVars.palette.whiteColor,
      })),
      container: getStyle('container', styles, () => ({
        height: '100%',
      })),
      control: getStyle('control', styles, (provided, state) => ({
        backgroundColor: state.isDisabled
          ? themeVars.palette.disabledBackgroundColor
          : backgroundColor || themeVars.palette.whiteColor,
        border: flat ? 'none' : null,
        borderRadius: borderRadius ? provided.borderRadius : 0,
        borderColor: state.isFocused
          ? `${themeVars.palette.primary1Color} !important`
          : themeVars.palette.borderColor,
        boxShadow: state.isFocused ? `0 0 0 1px ${themeVars.palette.primary1Color}` : provided.boxShadow,
        minHeight: 0,
        height: '100%',
        width: width || provided.width,
      })),
      singleValue: getStyle('singleValue', styles, () => ({
        color: themeVars.palette.primaryFontColor,
      })),
      input: getStyle('input', styles, () => ({
        color: themeVars.palette.primaryFontColor,
      })),
      multiValue: getStyle('multiValue', styles, () => ({
        backgroundColor: themeVars.palette.background2Color,
      })),
      multiValueLabel: getStyle('multiValueLabel', styles, () => ({
        color: themeVars.palette.primaryFontColor,
      })),
      multiValueRemove: getStyle('multiValueRemove', styles, () => ({
        color: themeVars.palette.lightTextColor,
      })),
      option: getStyle('option', styles, (provided, state) => ({
        backgroundColor: state.isFocused
          ? `${themeVars.palette.primary1HoverBackgroundColor} !important`
          : 'transparent',
        color: state.isSelected ? themeVars.palette.primaryFontColor : provided.color,
      })),
      indicatorsContainer: getStyle('indicatorsContainer', styles, provided => ({
        display: showArrow ? provided.display : 'none',
      })),
      indicatorSeparator: getStyle('indicatorSeparator', styles, provided => ({
        width: showSeparator ? provided.width : 0,
      })),
      noOptionsMessage: getStyle('noOptionsMessage', styles, () => ({
        textAlign: 'left',
      })),
      loadingMessage: getStyle('loadingMessage', styles, () => ({
        textAlign: 'left',
      })),
    }
  }

  getLabel = () => {
    const {label, floatingLabel, inlineElement} = this.props

    if (!isEmpty(label)) {
      let className = 'selectField-label'
      if (floatingLabel) className += ' selectField-label-floating'

      if (inlineElement) {
        return (
          <div className='selectField-inlineElementContainer'>
            <label className={className}>{label}</label>
            {inlineElement}
          </div>
        )
      }
      return <label className={className}>{label}</label>
    }

    return null
  }

  getClassName = () => {
    const {requiredClassName, className} = this.props

    const classes = ['container']
    if (!isEmpty(className))
      classes.push(className)

    if (!isEmpty(requiredClassName)) {
      classes.push(requiredClassName)
    }

    return getClassName(classes)
  }

  getComponents = (props) => {
    const components = {
      Option: this.getOptionComponent(),
    }

    const valueComponent = this.getValueComponent(props)
    if (valueComponent) {
      if (props.multi) {
        components.MultiValue = valueComponent
      } else {
        components.SingleValue = valueComponent
      }
    }

    return components
  }

  getSelectProps = () => {
    const neoProps = {...this.props}
    delete neoProps['style']
    delete neoProps['append']
    delete neoProps['async']
    delete neoProps['creatable']
    delete neoProps['errorText']
    delete neoProps['requiredClassName']
    delete neoProps['floatingLabel']
    delete neoProps['label']
    delete neoProps['OptionRenderer']

    const myProps = this.getProps(neoProps)

    const selectProps = {
      ...myProps,
      ref: this.selectRef,
      isMulti: myProps.multi,
      isDisabled: myProps.disabled || myProps.readOnly,
      isClearable: myProps.clearable,
      closeMenuOnSelect: !myProps.multi,
      styles: this.getStyles(myProps),
      components: this.getComponents(myProps),
      menuPortalTarget: document.querySelector('body'),
      value: getValue(myProps.value, myProps.options, myProps.multi),
      classNamePrefix: 'selectField',
      onChange: myProps.multi ? this.onMultiChange : this.onChange,
    }

    if (myProps.isOnlySearchLabelProperty) {
      selectProps.filterOption = (option, inputValue) => {
        return typeof option.label === 'string'
          ? option.label.toLowerCase().includes(inputValue.toLowerCase())
          : false
      }
    }

    return selectProps
  }

  onMultiChange = (value) => {
    const {onChange} = this.props
    onChange(isNil(value) ? [] : value)
  }

  onChange = (value) => {
    const {onChange} = this.props
    onChange(value)
  }

  render() {
    const {style, append, async, creatable, errorText} = this.props
    const props = this.getSelectProps()
    const select = this.getSelect(props, async, creatable)
    const post = (append !== null) ? append : null

    return (
      <div className={this.getClassName()} style={style}>
        {this.getLabel()}
        {select}
        {post}
        {errorText && <div className={'error-text'}>{errorText}</div>}
      </div>
    )
  }
}

SelectField.propTypes = {
  data: propTypes.any,
  children: propTypes.any,
  className: propTypes.string,
  label: propTypes.node,
  options: propTypes.arrayOf(propTypes.shape({
    value: propTypes.any,
    label: propTypes.oneOfType([
      propTypes.string,
      propTypes.object,
    ]),
    disabled: propTypes.bool,
  })),
  onChange: propTypes.func.isRequired,  // (selected: { value, label } )
  multi: propTypes.bool,
  async: propTypes.bool,
  creatable: propTypes.bool,
  loadOptions: propTypes.func,
  append: propTypes.element,
  readOnly: propTypes.bool,
  floatingLabel: propTypes.bool,
  style: propTypes.object,  // container style
  styles: propTypes.object, // see https://react-select.com/styles
  isOnlySearchLabelProperty: propTypes.bool,
  isOptionDisabled: propTypes.func,
  OptionRenderer: propTypes.func,
  ValueRenderer: propTypes.func,
  menuPlacement: propTypes.oneOf(['auto', 'top', 'bottom']),
  requiredClassName: propTypes.string,
  errorText: propTypes.node,
  clearable: propTypes.bool,
  width: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
  menuWidth: propTypes.number,
  flat: propTypes.bool,
  showSeparator: propTypes.bool,
  showArrow: propTypes.bool,
  backgroundColor: propTypes.string,
  borderRadius: propTypes.bool,
  inlineElement: propTypes.any,
  // For more options, refer to https://github.com/JedWatson/react-select
}

SelectField.defaultProps = {
  className: null,
  multi: false,
  async: false,
  creatable: false,
  isOnlySearchLabelProperty: false,
  OptionRenderer: null,
  menuPlacement: 'auto',
  showSeparator: true,
  showArrow: true,
  borderRadius: true,
}


export class MultiSelectField extends SelectField {
  getProps = (props) => {
    return {...props, multi: true, closeMenuOnSelect: false}
  }
}


export class AsyncSelectField extends Component {
  constructor(props) {
    super(props)
    this.selectRef = createRef()
  }

  focus = () => {
    if (this.selectRef.current) this.selectRef.current.focus()
  }

  getOptions = (input, callback) => {
    const {limit, minChars, params, fields, onLoad, onOptions} = this.props
    if (this.timer) window.clearTimeout(this.timer)

    if (minChars > 0 && (input == null || input.length < minChars)) {
      callback([])
    } else {
      this.timer = window.setTimeout(() => {
        onLoad({search: input, limit, ...params}, fields).then(response => {
          api.handleResponseNonYield(
            response,
            (key, data) => {
              callback(onOptions(data.result || []))
            },
            (key, message) => {
              console.error(message)
            }
          )
        })
      }, 250)
    }
  }

  getNoOptionsMessage = ({inputValue}) => {
    const {minChars} = this.props
    if (minChars > 0 && (inputValue == null || inputValue.length < minChars)) {
      return t(`Please type ${minChars} characters`)
    }
    return 'No results'
  }

  render() {
    const {placeholder, clearable, ...otherProps} = this.props

    const props = {
      clearable,
      placeholder,
      async: true,
      loadOptions: this.getOptions,
      noOptionsMessage: this.getNoOptionsMessage,
      ref: this.selectRef,
      ...otherProps,
    }

    return <SelectField {...props} />
  }
}

AsyncSelectField.propTypes = {
  limit: propTypes.number,
  minChars: propTypes.number,
  params: propTypes.oneOfType([
    propTypes.object,
    propTypes.array,
  ]),
  fields: propTypes.object,
  onLoad: propTypes.func.isRequired,    // ({ params }) return Promise()
  onOptions: propTypes.func.isRequired, // (loadResult) return [{ value, label }, ...]
  placeholder: propTypes.node,
  clearable: propTypes.bool,
  creatable: propTypes.bool,
}
AsyncSelectField.defaultProps = {
  limit: 10,
  minChars: 3,
  params: [],
  placeholder: 'Type to search',
  creatable: false,
}


export class CreatableField extends Component {
  getProps = (props) => {
    return props
  }

  focus = () => {
    this.select.focus()
  }

  render() {
    const {
      append,
      label,
      style,
      ...neoProps
    } = this.props
    const myProps = this.getProps(neoProps)

    const labelEl = (isEmpty(label))
      ? ''
      : (
        <label className='selectField-label'>
          {label}
        </label>
      )
    const post = (append !== null) ? append : null
    const select = <SelectField creatable={true} {...myProps}/>

    return (
      <div className='selectField-container' style={style}>
        {labelEl}
        {select}
        {post}
      </div>
    )
  }
}

CreatableField.propTypes = {
  label: propTypes.node,
  options: propTypes.arrayOf(propTypes.shape({
    value: propTypes.any.isRequired,
    label: propTypes.string.isRequired,
    disabled: propTypes.bool,
  })),
  onChange: propTypes.func.isRequired,  // (value)
  multi: propTypes.bool,
  async: propTypes.bool,
  style: propTypes.object,
  loadOptions: propTypes.func,
  append: propTypes.element,
}

export default {
  SelectField,
  MultiSelectField,
  AsyncSelectField,
  CreatableField,
}
